import React, { useEffect, useState } from 'react';
import { useForm, useFormState } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  Button,
  DrawerBody, DrawerFooter
} from '@chakra-ui/react';
import PropTypes from 'prop-types';

import Stepper from '../../../../../../components/Stepper';
import TrayHeader from '../../../../../../components/Tray/TrayHeader';
import { analyticsIds } from '../../../../../../constants/analyticsIds';
import {
  adminPageCreateUser,
  adminPageNewUserData,
  selectAdminPageNewUserData
} from '../../../../../../redux/modules/adminPage';
import { setFormIsDirty } from '../../../../../../redux/modules/form';
import { hideTray, updateTrayProps } from '../../../../../../redux/modules/tray';
import user from '../../../../../../redux/modules/user';
import { removePhoneNumberFormat } from '../../../../../../utils/formatPhoneUtil';

import InviteUser from './InviteUser';
import UserInfo from './UserInfo';
import UserPermissions from './UserPermissions';

const InfoStep = 'Information';
const PermissionsStep = 'Permissions';
const InviteStep = 'Invite';

const UserForm = () => {
  const dispatch = useDispatch();
  const { companyId } = useParams();
  const userSteps = [InfoStep, PermissionsStep, InviteStep];
  const newUserData = useSelector(selectAdminPageNewUserData);

  const useFormMethods = useForm({
    defaultValues: {
      firstName: null,
      lastName: null,
      email: null,
      phone: null,
      externalId: null,
      supervisor: null
    }
  });
  const { handleSubmit, control } = useFormMethods;
  const { isDirty } = useFormState({ control });

  useEffect(() => {
    dispatch(setFormIsDirty(isDirty));
    dispatch(updateTrayProps({ formIsDirty: isDirty }));
  }, [isDirty]);

  const [activeStep, setActiveStep] = useState(0);
  const isLastStep = activeStep === InviteStep;

  const renderStepContent = step => {
    switch (userSteps[step]) {
      case InfoStep:
        return <UserInfo step={activeStep} useFormMethods={useFormMethods} />;
      case PermissionsStep:
        return <UserPermissions companyId={companyId} step={activeStep} />;
      case InviteStep:
        return <InviteUser />;
      default:
        throw new Error('Invalid Step');
    }
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };

  const onSubmit = async data => {
    switch (userSteps[activeStep]) {
      case InfoStep: {
        dispatch(adminPageNewUserData({
          ...newUserData,
          userFirstName: data.firstName,
          userLastName: data.lastName,
          userEmail: data.email,
          userPhone: removePhoneNumberFormat(data.phone),
          externalId: data.externalId
        }));

        return setActiveStep(activeStep + 1);
      }

      case PermissionsStep: {
        if (newUserData.stagedRole && newUserData.stagedRole.departmentIds.length) {
          const addRole = newUserData.stagedRole;
          if (newUserData.roles) {
            dispatch(adminPageNewUserData({
              ...newUserData,
              roles: [...newUserData.roles, addRole],
              stagedRole: null
            }));
          } else {
            dispatch(adminPageNewUserData({
              ...newUserData,
              roles: [addRole],
              stagedRole: null
            }));
          }
        }

        return setActiveStep(activeStep + 1);
      }

      case InviteStep: {
        dispatch(adminPageCreateUser(newUserData));
        dispatch(adminPageNewUserData({}));
        return setTimeout(dispatch(hideTray()), 500);
      }

      default: {
        throw new Error('Invalid step');
      }
    }
  };

  return (
    <>
      <TrayHeader title="New User" />
      <DrawerBody pt={7}>
        <Stepper activeStep={activeStep} steps={userSteps} />
        <form id="userForm" onSubmit={handleSubmit(onSubmit)}>
          {renderStepContent(activeStep)}
        </form>
      </DrawerBody>
      <DrawerFooter justifyContent={activeStep === 0 ? 'flex-end' : 'space-between'} py={4}>
        {activeStep !== 0 && (
          <Button
            variant="secondary"
            onClick={handleBack}
          >
            Back
          </Button>
        )}
        <Button
          data-analytics-id={isLastStep ? analyticsIds.adminSettingsPage.users.inviteSaveUser : undefined}
          form="userForm"
          type="submit"
        >
          {isLastStep ? 'Invite' : 'Next'}
        </Button>
      </DrawerFooter>
    </>
  );
};

UserForm.propTypes = {
  department: PropTypes.shape({
    departmentName: PropTypes.string,
    departmentId: PropTypes.number,
    address: PropTypes.string,
    address2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.number
  })
};

export default UserForm;
